import { useCallback } from 'react';
import { NodePositionChange, XYPosition } from '@xyflow/react';
import { ObjectOperations } from '../objectOperations';
import { SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { Node } from '@xyflow/react';
import {
  CanvasGridPosition,
  convertXYPositionToGridIndex,
  snapPositionToGrid,
} from '../../helpers/gridPositioningUtils';
import { useGetNodeAbsolutePosition } from '../../helpers/useGetNodeAbsolutePosition';
import { getNodePositionInsideParent } from '../getNodePositionInsideParent';
import { ShortNodeInfo } from '../../normalizeGroupPositions';
import { adjustNodePositionForSection } from '../../canvas-element-tool/helper';
import { CanvasElementType } from '../../canvas-types';

const SNAP_TO_GRID_ELEMENTS = [CanvasElementType.GROUP, CanvasElementType.STANDALONE_NODE];

// Helper function to calculate position with section offset
export const calculatePositionWithOffset = (
  nodeType: CanvasElementType,
  position: { x: number; y: number },
) => {
  const canvasGridPosition = convertXYPositionToGridIndex(position, {
    withPaddingByNodeType: nodeType,
  });
  const { x, y } = adjustNodePositionForSection({ ...position });
  return { positionWithOffset: { x, y }, canvasGridPosition };
};

const nodeShouldSnapToGrid = (node: ShortNodeInfo) => SNAP_TO_GRID_ELEMENTS.includes(node.type);
export const maybeSnapToGridPosition = (node: ShortNodeInfo, position: XYPosition) =>
  nodeShouldSnapToGrid(node)
    ? snapPositionToGrid(position, { withPaddingByNodeType: node.type })
    : position;

export const maybeCalculatePositionWithOffset = (node: ShortNodeInfo, position: XYPosition) => {
  if (nodeShouldSnapToGrid(node)) {
    return calculatePositionWithOffset(node.type, position);
  }
  return {
    positionWithOffset: position,
    canvasGridPosition: convertXYPositionToGridIndex(position, {
      withPaddingByNodeType: node.type,
    }),
  };
};

// Helper function to update node position and parent
export const updateNodePosition = (
  nodeOperations: ObjectOperations<SweepCanvasRfNode>,
  nodeId: string,
  position: { x: number; y: number },
  parentId?: string,
) => {
  nodeOperations.setObject(nodeId, (node) => ({
    ...node,
    position,
    parentId,
  }));
};

export interface NodeMoveEvent {
  nodeId: string;
  xyPosition: XYPosition;
  canvasGridPosition: CanvasGridPosition;
  parentId?: string;
}

/**
 * Generic hook for creating node move events that can be used with any node type.
 * Handles position changes, parent relationships, and grid positioning.
 * Used for sectionable nodes. Nodes that can be moved within a section.
 *
 * @returns Object with createNodeMoveEvent function
 *
 * @example
 * // For standalone nodes
 * const { createNodeMoveEvent } = useSectionableNodeMoveEvent();
 * const moveEvent = createNodeMoveEvent({ nodeChange, node, nodeOperations });
 *
 * @example
 * // For regular nodes
 * const { createNodeMoveEvent } = useNodeMoveEvent();
 * const moveEvent = createNodeMoveEvent({ nodeChange, node, nodeOperations });
 */
export const useSectionableNodeMoveEvent = () => {
  const { getNodeAbsolutePosition } = useGetNodeAbsolutePosition();

  const createSectionableNodeMoveEvent = useCallback(
    ({
      nodeChange,
      node,
      nodeOperations,
      intersectionSectionNode,
    }: {
      nodeChange: NodePositionChange;
      node: ShortNodeInfo;
      nodeOperations: ObjectOperations<SweepCanvasRfNode>;
      intersectionSectionNode: Node | null;
      snapToGrid?: boolean;
    }): NodeMoveEvent | null => {
      if (intersectionSectionNode) {
        if (node.parentId) {
          if (intersectionSectionNode.id === node.parentId) {
            // Node is moving within the same parent
            const newPosition = maybeSnapToGridPosition(node, nodeChange.position ?? node.position);
            const { positionWithOffset, canvasGridPosition } = maybeCalculatePositionWithOffset(
              node,
              newPosition,
            );

            updateNodePosition(nodeOperations, node.id, positionWithOffset, node.parentId);
            nodeChange.position = positionWithOffset;

            return {
              nodeId: node.id,
              canvasGridPosition,
              xyPosition: newPosition,
              parentId: node.parentId,
            };
          }

          // Node is moving to a different parent
          const nodeAbsolutePosition = getNodeAbsolutePosition(node);
          if (!nodeAbsolutePosition) return null;

          const nodePositionInsideParent = getNodePositionInsideParent({
            node: { ...node, position: nodeAbsolutePosition },
            groupNode: intersectionSectionNode,
          });
          const newPosition = maybeSnapToGridPosition(node, nodePositionInsideParent);
          const { positionWithOffset, canvasGridPosition } = maybeCalculatePositionWithOffset(
            node,
            newPosition,
          );

          updateNodePosition(
            nodeOperations,
            node.id,
            positionWithOffset,
            intersectionSectionNode.id,
          );
          nodeChange.position = positionWithOffset;

          return {
            nodeId: node.id,
            canvasGridPosition,
            parentId: intersectionSectionNode.id,
            xyPosition: newPosition,
          };
        }

        // Node is moving from no parent to a parent
        const nodePositionInsideParent = getNodePositionInsideParent({
          node,
          groupNode: intersectionSectionNode,
        });
        const newPosition = snapPositionToGrid(nodePositionInsideParent, {
          withPaddingByNodeType: node.type,
        });
        const { positionWithOffset, canvasGridPosition } = maybeCalculatePositionWithOffset(
          node,
          newPosition,
        );

        updateNodePosition(nodeOperations, node.id, positionWithOffset, intersectionSectionNode.id);
        nodeChange.position = positionWithOffset;

        return {
          nodeId: node.id,
          canvasGridPosition,
          parentId: intersectionSectionNode.id,
          xyPosition: newPosition,
        };
      }

      // Node
      const absolutePosition = getNodeAbsolutePosition(node.id);
      if (!absolutePosition) return null;

      const xyPosition = maybeSnapToGridPosition(node, absolutePosition);
      updateNodePosition(nodeOperations, node.id, xyPosition, undefined);
      nodeChange.position = xyPosition;

      return {
        nodeId: node.id,
        canvasGridPosition: convertXYPositionToGridIndex(xyPosition, {
          withPaddingByNodeType: node.type,
        }),
        parentId: undefined,
        xyPosition: xyPosition,
      };
    },
    [getNodeAbsolutePosition],
  );

  return {
    createSectionableNodeMoveEvent,
  };
};
